import { useCallback, useRef, useState } from 'react';

import ReactCrop, { centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button } from 'components/FormComponents';

import styles from './ImageCropModal.module.scss';

const HANDLE_INSET = 12;

const makeMaxAspectCrop = (width, height, aspect) => {
  const imageAspect = width / height;

  if (imageAspect > aspect) {
    const cropHeight = height;
    const cropWidth = cropHeight * aspect;
    return centerCrop(
      { unit: 'px', width: cropWidth, height: cropHeight, x: 0, y: 0 },
      width,
      height,
    );
  }

  const cropWidth = width;
  const cropHeight = cropWidth / aspect;
  return centerCrop(
    { unit: 'px', width: cropWidth, height: cropHeight, x: 0, y: 0 },
    width,
    height,
  );
};

const insetCropForHandles = crop => {
  const shrink = Math.min(
    HANDLE_INSET,
    Math.floor((crop.width - 60) / 2),
    Math.floor((crop.height - 60) / 2),
  );

  if (shrink <= 0) return crop;

  return {
    ...crop,
    x: crop.x + shrink,
    y: crop.y + shrink,
    width: crop.width - shrink * 2,
    height: crop.height - shrink * 2,
  };
};

const toDisplayCrop = (crop, img) => {
  const scaleX = img.width / img.naturalWidth;
  const scaleY = img.height / img.naturalHeight;

  return {
    unit: 'px',
    x: crop.x * scaleX,
    y: crop.y * scaleY,
    width: crop.width * scaleX,
    height: crop.height * scaleY,
  };
};

const parseRecommendedSize = size => {
  if (!size) return null;
  const match = size.match(/(\d+)\s*[×x]\s*(\d+)/i);
  if (!match) return null;
  return { width: Number(match[1]), height: Number(match[2]) };
};

export const ImageCropModal = ({ imageSrc, aspect, recommendedSize, onConfirm, onCancel }) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [effectiveAspect, setEffectiveAspect] = useState(aspect);

  const onImageLoad = useCallback(
    e => {
      const img = e.currentTarget;
      const { naturalWidth: width, naturalHeight: height } = img;
      const maxCrop = makeMaxAspectCrop(width, height, aspect);
      const coversFullImage = maxCrop.width >= width - 1 && maxCrop.height >= height - 1;
      const recommended = parseRecommendedSize(recommendedSize);
      const isSmallerThanRecommended =
        recommended && width <= recommended.width && height <= recommended.height;

      if (isSmallerThanRecommended && !coversFullImage) {
        setEffectiveAspect(undefined);
        const fullCrop = { unit: 'px', x: 0, y: 0, width, height };
        const displayCrop = toDisplayCrop(insetCropForHandles(fullCrop), img);
        setCrop(displayCrop);
        setCompletedCrop(displayCrop);
        return;
      }

      setEffectiveAspect(aspect);
      const displayCrop = toDisplayCrop(insetCropForHandles(maxCrop), img);
      setCrop(displayCrop);
      setCompletedCrop(displayCrop);
    },
    [aspect, recommendedSize],
  );

  const getCroppedBlob = useCallback(() => {
    const image = imgRef.current;
    if (!image || !completedCrop) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement('canvas');
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }, [completedCrop]);

  const handleConfirm = async () => {
    const blob = await getCroppedBlob();
    if (blob) onConfirm(blob);
  };

  return (
    <AnimatedModal
      visible
      onClose={onCancel}
      title="Crop image"
      small
      description={recommendedSize ? 'Recommended size: ' + recommendedSize : undefined}>
      <div className={styles.cropOuter}>
        <div className={styles.cropInner}>
          <ReactCrop
            crop={crop}
            onChange={c => setCrop(c)}
            onComplete={c => setCompletedCrop(c)}
            aspect={effectiveAspect}
            keepSelection>
            <img
              ref={imgRef}
              src={imageSrc}
              onLoad={onImageLoad}
              className="max-h-[60vh] max-w-full object-contain"
              alt="Crop preview"
            />
          </ReactCrop>
        </div>
      </div>
      <div className="flex justify-end gap-4 mt-4">
        <Button type="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleConfirm} disabled={!completedCrop}>
          Apply crop
        </Button>
      </div>
    </AnimatedModal>
  );
};
